/**
 * FunnelVisualizer — main component for the funnel/customer journey visualization.
 * Contains the canvas, summary stats, and funnel controls.
 */
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { toLocalYmd } from "@/lib/date-format";
import { Card, CardContent } from "@/components/ui/card";
import {
  Users,
  DollarSign,
  TrendingUp,
  Layers,
  MousePointerClick,
  AlertCircle,
  Globe,
} from "lucide-react";
import type { FunnelNodeData, FunnelEdgeData, FunnelDefinition } from "./funnel-types";
import FunnelCanvas from "./funnel-canvas";
import { BlockSkeleton } from "@/components/skeletons";
import { Skeleton } from "@/components/ui/skeleton";

// ── Formatters ─────────────────────────────────────────

const formatCurrency = (value: number): string =>
  value.toLocaleString("pl-PL", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + " zł";

// ── Date helpers ───────────────────────────────────────

const computeDateRange = (days: number): { dateFrom: string; dateTo: string } => {
  const to = new Date();
  const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return { dateFrom: toLocalYmd(from), dateTo: toLocalYmd(to) };
};

// ── Summary stats calculation ──────────────────────────

interface FunnelStats {
  totalTraffic: number;
  totalRevenue: number;
  overallConversion: number;
  steps: number;
}

const calculateStats = (nodes: FunnelNodeData[]): FunnelStats => {
  const adNodes = nodes.filter((n) => n.type === "ad" || n.type === "landing");
  const totalTraffic = adNodes.reduce((sum, n) => sum + n.trafficIn, 0);

  const nodesWithRevenue = nodes.filter((n) => n.revenue !== undefined && n.revenue > 0);
  const totalRevenue = nodesWithRevenue.reduce((sum, n) => sum + (n.revenue ?? 0), 0);

  const thankYouNodes = nodes.filter((n) => n.type === "thank_you");
  const finalTraffic = thankYouNodes.reduce((sum, n) => sum + n.trafficIn, 0);
  const overallConversion = totalTraffic > 0 ? (finalTraffic / totalTraffic) * 100 : 0;

  return {
    totalTraffic,
    totalRevenue,
    overallConversion,
    steps: nodes.length,
  };
};

// ── Summary Card ───────────────────────────────────────

interface StatCardProps {
  icon: typeof Users;
  label: string;
  value: string;
  iconColor: string;
}

function StatCard({ icon: Icon, label, value, iconColor }: StatCardProps) {
  return (
    <Card className="bg-white/[0.03] border border-white/[0.06] rounded-xl">
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
          <span className="text-[10px] text-white/50 uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-xl font-bold text-white">{value}</div>
      </CardContent>
    </Card>
  );
}

// ── Main Component ─────────────────────────────────────

interface FunnelApiResult {
  nodes: FunnelNodeData[];
  edges: FunnelEdgeData[];
  totalVisitors: number;
  totalSessions: number;
  dateRange: { from: string; to: string };
}

export default function FunnelVisualizer() {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [canvasNodes, setCanvasNodes] = useState<FunnelNodeData[]>([]);
  const [canvasEdges, setCanvasEdges] = useState<FunnelEdgeData[]>([]);

  const { data, isLoading, error } = useQuery<FunnelApiResult>({
    queryKey: ["funnel-pages", days],
    queryFn: () => apiRequest("GET", `/api/tracking/funnel-pages?days=${days}`),
    staleTime: 5 * 60 * 1000,
  });

  const { dateFrom, dateTo } = computeDateRange(days);

  const { data: settingsData } = useQuery<{ ghlAttributionSource?: string }>({
    queryKey: ["analytics-settings-funnel"],
    queryFn: () => apiRequest("GET", "/api/analytics-settings"),
    staleTime: 5 * 60 * 1000,
  });
  const attributionSource = settingsData?.ghlAttributionSource || "ghl";

  const { data: ghlData } = useQuery<{ totalRevenue: number }>({
    queryKey: ["ghl-funnel-revenue", days, attributionSource],
    queryFn: () =>
      apiRequest("POST", "/api/integrations/crm/sales-data", {
        dateFrom,
        dateTo,
        attributionSource,
      }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  // Sync canvas state when API data loads
  useEffect(() => {
    if (data) {
      setCanvasNodes(data.nodes);
      setCanvasEdges(data.edges);
    }
  }, [data]);

  const stats = useMemo(() => calculateStats(canvasNodes), [canvasNodes]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-white/70 uppercase tracking-wider flex items-center gap-2">
          <Layers className="w-4 h-4 text-[#ef3e29]" />
          {t("dashboard.roasWorld.funnel.title")}
        </h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 p-0.5 bg-[#1a1614] border border-[#2e2624] rounded-lg">
            {([7, 14, 30, 60] as const).map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-2.5 py-1 text-[10px] font-medium rounded transition-colors ${
                  days === d ? "bg-[#ef3e29] text-white" : "text-white/40 hover:text-white"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          {canvasNodes.length > 0 && (
            <div className="flex items-center gap-1 text-[10px] text-white/30">
              <MousePointerClick className="w-3 h-3" />
              {t("dashboard.roasWorld.funnel.dragHint")}
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {/* VP-208: kafelki statystyk + obszar diagramu w wymiarach docelowych,
          żeby lejek nie „wskakiwał" po policzeniu ścieżek. */}
      {isLoading && (
        <div className="space-y-3">
          {/* Kafelki lustrują lokalny `StatCard` (Card + CardContent p-3, wartość
              text-xl) — `KpiTileSkeleton` jest wyskalowany pod duże kafelki
              dashboardu (px-6/pt-6) i byłby o ~60px za wysoki. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Skeleton className="h-3.5 w-3.5 rounded shrink-0" />
                    <Skeleton className="h-2.5 w-20" />
                  </div>
                  <Skeleton className="h-6 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
          <BlockSkeleton className="h-[420px] w-full" />
        </div>
      )}

      {/* Error */}
      {error && !isLoading && (
        <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-8 h-8 text-red-400/40 mx-auto mb-3" />
            <p className="text-sm text-white/50">{t("dashboard.roasWorld.funnel.loadError")}</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state — no tracking data yet */}
      {!isLoading && !error && canvasNodes.length === 0 && (
        <Card className="bg-white/[0.02] border border-white/[0.06] rounded-xl">
          <CardContent className="p-12 text-center">
            <Globe className="w-10 h-10 text-white/15 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-white/60 mb-2">
              {t("dashboard.roasWorld.funnel.noData")}
            </h3>
            <p className="text-xs text-white/35 max-w-sm mx-auto">
              {t("dashboard.roasWorld.funnel.noDataHint")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Real data — summary + canvas */}
      {!isLoading && canvasNodes.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              icon={Users}
              label="Ruch wejściowy"
              value={stats.totalTraffic.toLocaleString()}
              iconColor="text-blue-400/60"
            />
            <StatCard
              icon={TrendingUp}
              label="Konwersja lejka"
              value={`${stats.overallConversion.toFixed(1)}%`}
              iconColor="text-emerald-400/60"
            />
            <StatCard
              icon={DollarSign}
              label="Przychód lejka"
              value={
                ghlData?.totalRevenue != null && ghlData.totalRevenue > 0
                  ? formatCurrency(ghlData.totalRevenue)
                  : stats.totalRevenue > 0
                    ? formatCurrency(stats.totalRevenue)
                    : "—"
              }
              iconColor="text-amber-400/60"
            />
            <StatCard
              icon={Layers}
              label="Stron w lejku"
              value={String(stats.steps)}
              iconColor="text-purple-400/60"
            />
          </div>
          <FunnelCanvas nodes={canvasNodes} edges={canvasEdges} onNodesChange={setCanvasNodes} />
        </>
      )}
    </div>
  );
}
